import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'

import { useSubmitInternationalAddressMutation } from '@twisto/api-graphql'
import { FormSubmitButton } from '@twisto/components/atoms/button'
import type { CastedSubmitHandler } from '@twisto/components/atoms/form'
import { useForm } from '@twisto/components/atoms/form'
import {
  Headline,
  HeadlineSubtitle,
  HeadlineTitle,
} from '@twisto/components/atoms/headline'
import { TextField } from '@twisto/components/molecules/text-field'
import { useResponseErrors, validator } from '@twisto/form'

import { FlowForm, submittingFlowFormAtom } from '../../components'
import { useFlow } from '../../flow/flow.context'

import * as styles from './international-address-step.styles'

const fieldNames = {
  city: 'city',
  zipCode: 'zipCode',
  street: 'street',
} as const

type FormValues = {
  [fieldNames.city]: string
  [fieldNames.zipCode]: string
  [fieldNames.street]: string
}

export const InternationalAddressStep = () => {
  const [submitting] = useAtom(submittingFlowFormAtom)
  const { flow, sessionId } = useFlow<'InternationalAddressStep'>()
  const { city, street, zipCode } = flow.currentStep

  const formContext = useForm({
    defaultValues: {
      [fieldNames.city]: city.value ?? '',
      [fieldNames.street]: street.value ?? '',
      [fieldNames.zipCode]: zipCode.value ?? '',
    },
  })

  const [submitInternationalAddress, { data }] =
    useSubmitInternationalAddressMutation()

  // handle field errors - such a invalid email
  useResponseErrors(data?.submitInternationalAddress, formContext)

  const handleSubmit: CastedSubmitHandler<FormValues> = async ({
    city,
    street,
    zipCode,
  }: FormValues) =>
    submitInternationalAddress({
      variables: { input: { city, street, zipCode }, auth: { sessionId } },
    })

  return (
    <>
      <Headline align="start" css={styles.headlineSection}>
        <HeadlineTitle>
          <Trans id="flow.internationalAddressStep.title" />
        </HeadlineTitle>
        <HeadlineSubtitle>
          <Trans id="flow.internationalAddressStep.subtitle" />
        </HeadlineSubtitle>
      </Headline>
      <FlowForm {...formContext} onSubmit={handleSubmit}>
        <TextField
          autoFocus
          disabled={submitting}
          inputProps={{
            readOnly: street.readonly,
          }}
          label={<Trans id="flow.internationalAddressStep.street" />}
          name={fieldNames.street}
          rules={{
            required:
              street.required && validator.getValidationMessages().required,
          }}
          variant="input"
        />
        <div css={styles.inputRow}>
          <div>
            <TextField
              disabled={submitting}
              inputProps={{
                readOnly: city.readonly,
              }}
              label={<Trans id="flow.internationalAddressStep.city" />}
              name={fieldNames.city}
              rules={{
                required:
                  city.required && validator.getValidationMessages().required,
              }}
              variant="input"
            />
          </div>
          <div>
            <TextField
              disabled={submitting}
              inputProps={{
                readOnly: zipCode.readonly,
              }}
              label={<Trans id="flow.internationalAddressStep.zipCode" />}
              name={fieldNames.zipCode}
              rules={{
                required:
                  zipCode.required &&
                  validator.getValidationMessages().required,
              }}
              variant="input"
            />
          </div>
        </div>
        <FormSubmitButton>
          <Trans id="flow.internationalAddressStep.submitButton" />
        </FormSubmitButton>
      </FlowForm>
    </>
  )
}
